import { Chip, Icon, List } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { SyntheticEvent, useDeferredValue, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import defaultThumbnail from 'src/images/thumbnail-default.jpg';
import BusyIcon from 'src/js/component/base/busy-icon';
import { Ellipsis } from 'src/js/component/base/ellipsis';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { startStopStream } from 'src/js/pages/streaming/stream/stream-store';
import { AudioAlgorithmEnum, getAudioInterfaceLabels } from 'src/js/pages/streaming/audio-encoder/audio-encoder-models';
import audioEncoders from 'src/js/pages/streaming/audio-encoder/audio-encoder-store';
import { scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import { VideoEncoderState } from 'src/js/pages/streaming/video-encoder/video-encoder-models';
import videoEncoders from 'src/js/pages/streaming/video-encoder/video-encoder-store';
import { useWatchRenderOnChange } from 'src/js/store/use-watch';
import { constant } from 'src/js/constant';

import { StreamEdit } from './stream-edit';
import { StreamState, StreamViewModel, streamIsStopped } from './stream-models';
import { StreamStatistics } from './stream-statistics';
import {
  streamListStreamName,
  streamStateMessage,
  streamStateToColor,
  streamStateToIcon,
} from './stream-view-model-mapper';
import systemInfo from 'src/js/data/systemInfo';

interface Props {
  model: StreamViewModel;
  scrollToId: number | string;
  onSelect: (item: StreamViewModel, selected: boolean) => void;
}

export const StreamRow: React.FunctionComponent<Props> = ({ model: item, scrollToId, onSelect }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionStore } = useStores();

  useWatchRenderOnChange(videoEncoders);
  useWatchRenderOnChange(audioEncoders);

  const location = useLocation();
  const showStats = location?.state?.stats === true;

  const model = useDeferredValue(item);
  const scroll = location?.state?.id === model.id || scrollToId === model.id;

  const [isExpanded, setIsExpanded] = useState<boolean>(scroll);
  const [collapseTimeoutId, setCollapseTimeoutId] = useMutable(null);
  const [expandContent, setExpandContent] = useState<string>(scroll ? (showStats ? 'STATISTICS' : 'SETTINGS') : null);
  const onExpandedStateChange = (expandedState: { eventKey: any; expanded: boolean }) => {
    if (isExpanded !== expandedState.expanded) {
      setIsExpanded(expandedState.expanded);
    }
  };

  watchEvent('expandAll:edit', () => {
    setExpandContent('SETTINGS');
    setIsExpanded(true);
  });
  watchEvent('expandAll:stats', () => {
    setExpandContent('STATISTICS');
    setIsExpanded(true);
  });
  const collapseAll = () => {
    setIsExpanded(false);
    setCollapseTimeoutId(
      setTimeout(() => {
        // the component needs to stay rendered during close for smooth closing
        setExpandContent(null);
        setCollapseTimeoutId(null);
      }, constant.collapseTime),
    );
  };
  watchEvent('collapseAll:edit', collapseAll);
  watchEvent('collapseAll:stats', collapseAll);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const onSelectHandler = (eventKey: string, expanded: boolean, ev: KeyboardEvent) => {
    switch (eventKey) {
      case 'STATISTICS':
      case 'SETTINGS':
        if (ev && ev.shiftKey) {
          // expend all
          if (expanded) {
            eventKey === 'SETTINGS' ? triggerEvent('expandAll:edit') : triggerEvent('expandAll:stats');
          } else {
            eventKey === 'SETTINGS' ? triggerEvent('collapseAll:edit') : triggerEvent('collapseAll:stats');
          }
          return;
        }
        setExpandContent(eventKey);
        if (expanded) {
          setIsExpanded(true);
          if (collapseTimeoutId() !== null) {
            clearTimeout(collapseTimeoutId());
            setCollapseTimeoutId(null); // prevent closing of panel
          }
          scroll && scrollPanelIntoView({ id: model.id });
        } else {
          setIsExpanded(false);
          setCollapseTimeoutId(
            setTimeout(() => {
              // the component needs to stay rendered during close for smooth closing
              setExpandContent(null);
              setCollapseTimeoutId(null);
            }, constant.collapseTime),
          );
        }
        break;
      case 'PLAY':
        setIsUpdating(true);
        if (!streamIsStopped(model.state)) {
          setRealThumbnailHidden(true);
          setDefaultThumbnailHidden(false);
        }
        startStopStream(model, streamIsStopped(model.state), t).then(() => {
          triggerEvent('preset-update');
          setIsUpdating(false);
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    return () => {
      // final cleanup
      if (collapseTimeoutId() !== null) {
        clearTimeout(collapseTimeoutId());
      }
    };
  }, []);

  const videoEncoder = videoEncoders.find((v) => (model.video ? v.id === model.video[0]?.id : false));

  // thumbnail refresh
  const [thumbnailDate, setThumbnailDate] = useState<string>(`${new Date().getTime()}`);
  useEffect(() => {
    let endrun = false;
    let timer: any;
    const triggerRefresh = () => {
      if (!endrun) {
        setThumbnailDate(`${new Date().getTime()}`);
        if (videoEncoder?.previewIntervalSec >= 1) {
          timer = setTimeout(triggerRefresh, videoEncoder?.previewIntervalSec * 1000);
        }
      }
    };

    timer = setTimeout(() => {
      triggerRefresh();
    }, (videoEncoder?.previewIntervalSec || 10) * 1000);

    return () => {
      // cleanup
      endrun = true;
      clearTimeout(timer);
    };
  }, [videoEncoder?.previewIntervalSec]);

  const snapshotRef = useRef<HTMLImageElement>();
  const [defaultThumbnailHidden, setDefaultThumbnailHidden] = useState(false);
  const [realThumbnailHidden, setRealThumbnailHidden] = useState(true);

  const onLoadThumbnail = (e: SyntheticEvent<HTMLImageElement, Event>) => {     
    
    if (e.type !== 'error' && snapshotRef?.current) {
      snapshotRef.current.style.visibility = 'visible';
 
      if (model.state !== StreamState.STOPPED) { 
        setDefaultThumbnailHidden(true);
        setRealThumbnailHidden(false);

      }
      return;
    }
    setDefaultThumbnailHidden(false);
    setRealThumbnailHidden(true);
  };

  useEffect(() => {
    if (model.state !== StreamState.RUNNING || !videoEncoder?.previewEnabled) {

      setDefaultThumbnailHidden(false);
      setRealThumbnailHidden(true);
    }
  }, [model.state, videoEncoder?.previewEnabled]);

  const audioEncoder = audioEncoders.find((a) => (model.audio ? a.id === model.audio[0]?.id : false));

  const handleClickVideo = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/videnc/', { state: { id: id } });
  };

  const handleClickAudio = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/audenc/', { state: { id: id } });
  };

  const handleClickMetadata = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/metadata/', { state: { id: id } });
  };

  const handleClickScte = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/scte/', { state: { id: id } });
  };

  const btnId = `stream-settings-${model.id}`;
  const handleCancelEdit = () => {
    const btn = document.getElementById(btnId);
    if (!isNil(btn)) {
      btn.click();
    }
  };

  const onClickListPanel = (ev: React.MouseEvent<HTMLDivElement>) => {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (
      (ev.target as any)?.className !== undefined /*
      (ev.target as any)?.className?.includes?.('HaiListPanelTitle') ||
      (ev.target as any)?.className?.includes?.('list-panel-main-content')*/
    ) {
      if (expandContent == null) {
        setExpandContent('SETTINGS');
      }
      // so we dont expand empty content
      setIsExpanded(!isExpanded);
    }
  };

  const statusColor = streamStateToColor(model.state);
  const streamMsg = streamStateMessage(model, videoEncoder, t);
  const rejectReason = model?.stats?.srt?.rejectedReason;
  let rejectReasonClean;
  let pos;
  if (rejectReason && (pos = rejectReason.indexOf('('))) {
    rejectReasonClean = rejectReason.substring(pos + 1, rejectReason.length - 1);
  }
  return (
    <div id={`panel-${model.id}`} className={model.hidden ? 'mk-row-hidden' : ''} style={{ scrollMarginTop: '21px' }}>
      <List.Panel
        onClick={onClickListPanel}
        onSelect={(selected: boolean) => onSelect(model, selected)}
        checked={model.selected}
        panelColor={statusColor}
        expandedState={{ eventKey: expandContent, expanded: isExpanded }}
        onExpandedStateChange={onExpandedStateChange}
      >
        <List.PanelTitle
          useFsMask={true}
          onClick={onClickListPanel}
          title={streamListStreamName(t, model.name)}
          statusIcon={streamStateToIcon(model.state)}
          iconColor={statusColor}
          iconTooltip={rejectReasonClean}
        />

        {model.state === StreamState.RUNNING &&
          videoEncoder?.stats?.state === VideoEncoderState.WORKING &&
          videoEncoder?.previewEnabled === true && (
            <List.Thumbnail
              useFsMask
              key={"real-thumbnail"}
              src={`/apis/preview/${videoEncoder?.interface}?t=${thumbnailDate}`}
              onError={onLoadThumbnail}
              onLoad={onLoadThumbnail}
              componentRef={snapshotRef}
              hidden={model.hidden || realThumbnailHidden}
            />
          )}
        <List.Thumbnail
          src={defaultThumbnail}
              key={"fake-thumbnail"}
          hidden={model.hidden || defaultThumbnailHidden}
          overlayText={
            (model.state !== StreamState.RUNNING ||
              videoEncoder?.stats?.state !== VideoEncoderState.WORKING ||
              !videoEncoder?.previewEnabled) &&
            streamMsg
          }
          overlayStyles={{ backgroundColor: 'transparent' }}
        />

        <List.PanelDetail>
          <List.PanelDetailItem divider="vertical" text={t('general.id')}>
            <Chip size="sm">{model.id.toString()}</Chip>
          </List.PanelDetailItem>
          <List.PanelDetailItem divider="vertical" text={model.encapsulationTextual} dataAuto="stream_protocol" />
          {systemInfo.hasEth1 && (
            <List.PanelDetailItem
              text={
                <>
                  <Icon iconname="Ethernet" size="sm3" />
                  {'\u00A0'}
                  {t(`stream.row.networkInterface.${model.networkInterface}`)}
                </>
              }
              collapseText={t(`stream.row.networkInterface.${model.networkInterface}`)}
              dataAuto="network_interface"
              divider="vertical"
            />
          )}

          <List.PanelDetailItem
            divider="vertical"
            className="fs-mask"
            text={<Ellipsis maxWidth="calc(max(200px, 22vw)" 
            onClick={(e) => onClickListPanel(e)} >{model.destination}</Ellipsis>}
          />
          {/* video */}
          {videoEncoder && <List.PanelDetailItem text={<Icon iconname="Video" size="sm3" />} />}
          {videoEncoder && (
            <List.PanelDetailItem
              text={
                <Chip
                  size="sm"
                  tooltip={
                    <>
                      <div>{videoEncoder.interfaceTxt}</div>
                      {videoEncoder?.name}
                    </>
                  }
                  id={videoEncoder.id}
                  onClick={handleClickVideo}
                >
                  {videoEncoder.id}
                </Chip>
              }
            />
          )}
          {videoEncoder && <List.PanelDetailItem divider="dot" text={model.videoOutputResolution} />}
          {videoEncoder && <List.PanelDetailItem divider="dot" text={model.codecAlgorithm} />}
          {videoEncoder && <List.PanelDetailItem divider={audioEncoder && 'dot'} text={model.videoBitrate} />}
          {/* audio */}
          {audioEncoder && <List.PanelDetailItem text={<Icon iconname="AudioAlt" size="sm3" />} />}
          {model.audio?.length > 0 &&
            model.audio?.map((audenc, index) => {
              const interfaceTxt = getAudioInterfaceLabels(audenc.interface, audenc.layout, audenc.codec).interfaceStr;
              return (
                <List.PanelDetailItem
                  key={`audenc-${index}`}
                  text={
                    <Chip
                      size="sm"
                      tooltip={
                        <>
                          <div>{interfaceTxt}</div>
                          {audenc.name}
                        </>
                      }
                      id={audenc.id}
                      onClick={handleClickAudio}
                    >
                      {audenc.id}
                    </Chip>
                  }
                />
              );
            })}
          {audioEncoder && audioEncoder.algorithm !== AudioAlgorithmEnum.RAW && (
            <List.PanelDetailItem text={model.audioBitrate} />
          )}
          {model.metadata?.length > 0 && <List.PanelDetailItem text={<Icon iconname="Metadata" size="sm3" />} />}
          {model.metadata?.length > 0 &&
            model.metadata?.map((m, index) => (
              <List.PanelDetailItem
                key={`metadata-${index}`}
                text={
                  <Chip
                    key={m.id}
                    size="sm"
                    tooltip={
                      <>
                        <div>{m.name}</div>
                        {t('dashboard.metadata')}
                      </>
                    }
                    id={m.id}
                    onClick={handleClickMetadata}
                  >
                    {m.id}
                  </Chip>
                }
              />
            ))}
          {model.scte?.length > 0 && <List.PanelDetailItem text={<Icon iconname="Device" size="sm3" />} />}
          {model.scte?.length > 0 &&
            model.scte?.map((m, index) => (
              <List.PanelDetailItem
                key={`scte-${index}`}
                text={
                  <Chip
                    key={m.id}
                    size="sm"
                    tooltip={
                      <>
                        <div>{m.name}</div>
                        {t('dashboard.scte')}
                      </>
                    }
                    id={m.id}
                    onClick={handleClickScte}
                  >
                    {m.id}
                  </Chip>
                }
              />
            ))}
        </List.PanelDetail>

        {sessionStore.isUser() && (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
          </List.Actions>
        )}
        {!sessionStore.isUser() && (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
            <List.ActionItem isDivider />
            <List.ActionItem
              eventKey="PLAY"
              icon={
                <BusyIcon
                  isActive={!streamIsStopped(model.state)}
                  activeIcon="StopFilled"
                  inactiveIcon="PlayFilled"
                  busy={isUpdating}
                />
              }
              showActive={false}
              onSelect={onSelectHandler}
              disabled={isUpdating}
            />
          </List.Actions>
        )}
        <List.ExpandedPanel>
          {expandContent === 'STATISTICS' && isExpanded && (
            <StreamStatistics model={model} showDetailedButton={true} isExpanded={isExpanded} parent="STREAM" />
          )}
          {expandContent === 'SETTINGS' && isExpanded && <StreamEdit model={model} onCancel={handleCancelEdit} />}
        </List.ExpandedPanel>
      </List.Panel>
    </div>
  );
};
